import { useCallback, useEffect, useRef, useState } from 'react';

import PropTypes from 'prop-types';
import { BsInfoCircle, BsCheckCircle, BsXCircle, BsDashCircle } from 'react-icons/bs';

const HOVER_CLOSE_DELAY_MS = 200;

const AIScoreBadge = ({ score, criteria, companyName }) => {
	const [showTooltip, setShowTooltip] = useState(false);
	const [activeCriterionIndex, setActiveCriterionIndex] = useState(null);
	const closeTimerRef = useRef(null);
	const leavingIndexRef = useRef(null);

	const cancelClose = useCallback(() => {
		if (closeTimerRef.current) {
			clearTimeout(closeTimerRef.current);
			closeTimerRef.current = null;
		}
	}, []);

	// Grace period so the pointer can cross the gap between a criterion row and
	// its reasoning overlay without the hover state collapsing mid-move.
	const scheduleClose = useCallback(
		(closeAll) => {
			cancelClose();
			closeTimerRef.current = setTimeout(() => {
				// Moving between rows fires the old row's leave after the new row's
				// enter, so only retract a criterion that is still the active one.
				setActiveCriterionIndex((current) =>
					closeAll || leavingIndexRef.current === current ? null : current,
				);
				if (closeAll) setShowTooltip(false);
			}, HOVER_CLOSE_DELAY_MS);
		},
		[cancelClose],
	);

	useEffect(() => cancelClose, [cancelClose]);

	const getStatusIcon = (status) => {
		switch (status?.toLowerCase()) {
			case 'erfüllt':
				return { icon: BsCheckCircle, color: 'text-success-700', bgColor: 'bg-green-50' };
			case 'nicht erfüllt':
				return { icon: BsXCircle, color: 'text-warning-700', bgColor: 'bg-red-50' };
			case 'teilweise erfüllt':
				return { icon: BsDashCircle, color: 'text-caution-700', bgColor: 'bg-yellow-50' };
			default:
				return { icon: BsInfoCircle, color: 'text-text', bgColor: 'bg-gray-50' };
		}
	};

	const getScoreColor = (score) => {
		if (score >= 90) return 'bg-success-200 text-success-700 hover:border-success-700';
		if (score >= 70) return 'bg-primary-200 text-primary-500 hover:border-primary-500';
		if (score >= 50) return 'bg-caution-200 text-caution-700 hover:border-caution-700';
		return 'bg-warning-200 text-warning-700 hover:border-warning-700';
	};

	const activeCriterion = Array.isArray(criteria) ? criteria[activeCriterionIndex] : null;

	if (score === null || score === undefined) {
		return (
			<span className="inline-flex items-center px-3 py-1 text-xs rounded-full border border-transparent bg-bgBase-400 text-bgBase-800">
				Keine
			</span>
		);
	}

	return (
		<div
			className="relative inline-block"
			onMouseEnter={() => {
				cancelClose();
				setShowTooltip(true);
			}}
			onMouseLeave={() => {
				leavingIndexRef.current = activeCriterionIndex;
				scheduleClose(true);
			}}
		>
			<div
				className={`inline-flex items-center px-3 py-1 text-xs whitespace-nowrap rounded-full border border-transparent cursor-help transition-colors ${getScoreColor(score)}`}
			>
				AI-Analyse: {Math.round(score)}%
			</div>

			{showTooltip && (
				<div className="absolute z-50 w-72 pt-2 -right-8 top-full">
					<div className="bg-white rounded-lg shadow-lg border border-gray-200 p-3 text-sm">
						<div className="font-medium text-text mb-2">AI-Analyse für {companyName}</div>
						<div className="text-text mb-3">Score: {Math.round(score)}% Übereinstimmung</div>

						{Array.isArray(criteria) && criteria.length > 0 ? (
							<div className="space-y-2 relative">
								<div className="font-medium text-text text-xs uppercase tracking-wide">
									Bewertungskriterien:
								</div>
								<div className="space-y-2 max-h-64 overflow-y-auto">
									{criteria.map((item, index) => {
										const { icon: StatusIcon, color, bgColor } = getStatusIcon(item.status);
										const isActive = activeCriterionIndex === index;
										return (
											<div
												key={index}
												onMouseEnter={() => {
													cancelClose();
													setActiveCriterionIndex(index);
												}}
												onMouseLeave={() => {
													leavingIndexRef.current = index;
													scheduleClose(false);
												}}
											>
												<div
													className={`flex items-center gap-2 p-2 rounded-md ${bgColor} cursor-help transition-all ${isActive ? 'shadow-sm' : ''}`}
												>
													<StatusIcon className={`text-sm flex-shrink-0 ${color}`} />
													<div className="flex-1 min-w-0">
														<div className="text-xs font-medium text-text">
															{item.criterionName ||
																item.criterion ||
																'Unbekanntes Kriterium'}
														</div>
														<div className={`text-xs ${color} capitalize`}>
															{item.status}
														</div>
													</div>
												</div>
											</div>
										);
									})}
								</div>

								{activeCriterion?.reasoning && (
									<div
										className="absolute right-full top-0 z-50 pr-2"
										onMouseEnter={cancelClose}
										onMouseLeave={() => {
											leavingIndexRef.current = activeCriterionIndex;
											scheduleClose(false);
										}}
									>
										<div className="w-72 max-h-64 overflow-y-auto p-2 bg-gray-900 text-white text-xs rounded shadow-lg">
											<div className="font-medium mb-1">Begründung:</div>
											<div className="leading-relaxed">{activeCriterion.reasoning}</div>
											{activeCriterion.evidence &&
												activeCriterion.evidence !== 'Keine Evidenz gefunden' && (
													<div className="mt-2 pt-2 border-t border-gray-700">
														<div className="font-medium mb-1">Evidenz:</div>
														<div className="leading-relaxed italic text-bgBase-800">
															{activeCriterion.evidence}
														</div>
													</div>
												)}
										</div>
									</div>
								)}
							</div>
						) : (
							criteria && (
								<div className="space-y-1">
									<div className="font-medium text-text text-xs uppercase tracking-wide">
										Bewertungskriterien:
									</div>
									<div className="text-xs text-text leading-relaxed">
										{typeof criteria === 'string' ? criteria : JSON.stringify(criteria)}
									</div>
								</div>
							)
						)}

						<div className="mt-3 pt-2 border-t border-gray-100">
							<div className="text-xs text-bgBase-800">
								Basierend auf KI-Analyse der Unternehmensdetails
							</div>
						</div>

						{/* Arrow */}
						<div className="absolute top-0 right-8 w-2 h-2 bg-white border-l border-t border-gray-200 transform rotate-45 -translate-y-1/2" />
					</div>
				</div>
			)}
		</div>
	);
};

AIScoreBadge.propTypes = {
	score: PropTypes.number,
	criteria: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.arrayOf(
			PropTypes.shape({
				status: PropTypes.string,
				criterionName: PropTypes.string,
				criterion: PropTypes.string,
				reasoning: PropTypes.string,
				evidence: PropTypes.string,
			}),
		),
	]),
	companyName: PropTypes.string.isRequired,
};

AIScoreBadge.defaultProps = {
	score: null,
	criteria: null,
};

export default AIScoreBadge;
